import {useCallback, useEffect, useState} from "react";
import {useUser} from "../contexts/AuthContext";
import {get} from "aws-amplify/api";
import {Card, CardHeader, CardBody, Row, Col, Button, CardFooter} from "reactstrap";
import {BackgroundColorContext} from "../contexts/BackgroundColorContext";
import {RingLoader} from "react-spinners";
import ActivitiesCollection from "../components/Activities/ActivitiesCollection";
import {Activity} from "@zwiftalizer/parser";
import PageHeader from "../components/Common/PageHeader";
import AuthRequiredView from "../components/Common/AuthRequiredView";

const UserFiles = () => {
  const [activitiesData, setActivitiesData] = useState<Activity[]>([]);
  const [lastEvaluatedKey, setLastEvaluatedKey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [showEndMessage, setShowEndMessage] = useState(false);
  const {user, isInitialized} = useUser();

  const getData = useCallback(
    async (lastKey = null) => {
      try {
        // Set appropriate loading state based on whether we're loading more or initial load
        if (lastKey) {
          setLoadingMore(true);
        } else {
          setLoading(true);
        }

        const params = {
          headers: {
            Authorization: user.jwtToken
          },
          queryParams: lastKey ? {lastKey: encodeURIComponent(JSON.stringify(lastKey))} : undefined
        };
        const path = "/getUserFilesListing";
        const {body} = await get({
          apiName: "app",
          path: path,
          options: params
        }).response;
        const data = await body.json();
        const typedData = data as any;

        // Append activities to the list
        if (typedData.activities && typedData.activities.length > 0) {
          setActivitiesData((prevData) => [...prevData, ...typedData.activities]);
        }

        // Handle pagination state
        if (!typedData.lastEvaluatedKey) {
          // No more pages available
          setLastEvaluatedKey(null);
          if (lastKey) {
            // User clicked "Load More" but there were no more results
            setShowEndMessage(true);
            // Hide message after 3 seconds
            setTimeout(() => setShowEndMessage(false), 3000);
          }
        } else {
          setLastEvaluatedKey(typedData.lastEvaluatedKey);
        }
      } catch {
        // console.error(`error getting activities data: ${err}`);
      } finally {
        // Reset appropriate loading state
        if (lastKey) {
          setLoadingMore(false);
        } else {
          setLoading(false);
        }
      }
    },
    [user.jwtToken]
  );

  // useEffect to call getData when the component mounts and auth is initialized
  useEffect(() => {
    if (isInitialized && user.authenticated && user.jwtToken) {
      getData();
    } else if (isInitialized && !user.authenticated) {
      // User is not authenticated, stop loading
      setLoading(false);
    }
  }, [isInitialized, user.authenticated, user.jwtToken, getData]);

  // Show loading spinner first, before any other checks
  if (loading) {
    return (
      <div className="iconWrapper">
        <RingLoader color="#4682b4" size={80} loading={true} />
      </div>
    );
  }

  // Check if user is authenticated after loading is complete
  if (isInitialized && !user.authenticated) {
    return (
      <>
        <PageHeader title="My Log Reports" subtitle="Sign in to view your personal log history" icon="fa fa-file-alt" />
        <Row>
          <Col md="12">
            <AuthRequiredView
              title="Access Your Personal Log History"
              subtitle="Sign in to view and manage all your log reports"
              features={[
                {
                  icon: "fa-history",
                  title: "Activity History",
                  description: "View all your uploaded sessions in one place",
                  items: ["Chronological activity log", "Quick access to past reports", "Performance trends over time"]
                },
                {
                  icon: "fa-chart-line",
                  title: "Detailed Reports",
                  description: "Access comprehensive analysis for every uploaded session",
                  items: ["Network performance metrics", "Graphics FPS analysis", "Sensor connectivity data"]
                },
                {
                  icon: "fa-sync-alt",
                  title: "Re-analyze Anytime",
                  description: "Revisit past sessions without re-uploading files",
                  items: ["Permanent report storage", "Compare multiple sessions", "Track improvements"]
                }
              ]}
            />
          </Col>
        </Row>
      </>
    );
  }

  return (
    <BackgroundColorContext.Consumer>
      {({color}) => (
        <>
          <PageHeader
            title="My Log Reports"
            subtitle="Browse and analyze your uploaded log reports"
            icon="fa fa-file-alt"
          />
          <Row>
            <Col md="12">
              <Card>
                <CardHeader data-color={color}>
                  <h5 className="card-category mb-2">
                    Click the <i className="fa fa-eye" /> button to view the log report in a new tab.
                  </h5>
                  <h5 className="card-category">
                    A log report is the result of parsing your log file. Your original log files are not stored.
                  </h5>
                </CardHeader>
                <CardBody>
                  <Row>
                    <Col md="12">
                      <ActivitiesCollection items={activitiesData} color={color} />
                    </Col>
                  </Row>
                </CardBody>
                <CardFooter>
                  <Row>
                    <Col md="12">
                      <div className="float-right">
                        {showEndMessage && (
                          <span className="text-muted mr-3">
                            <i className="fa fa-check-circle" /> All files loaded
                          </span>
                        )}
                        {lastEvaluatedKey && (
                          <Button
                            color="primary"
                            data-color={color}
                            onClick={() => getData(lastEvaluatedKey)}
                            disabled={loadingMore}
                          >
                            {loadingMore ? (
                              <>Loading...</>
                            ) : (
                              <>
                                Load more <i className="fa fa-angle-right" />
                              </>
                            )}
                          </Button>
                        )}
                      </div>
                    </Col>
                  </Row>
                </CardFooter>
              </Card>
            </Col>
          </Row>
        </>
      )}
    </BackgroundColorContext.Consumer>
  );
};

export default UserFiles;
